import { Controller } from '@hotwired/stimulus'

/**
 * Фильтрует уже отрисованный список чатов сообщества без сетевых запросов.
 *
 * Варианты фильтра — стандартные toggle_option, то есть обычные чекбоксы:
 * состояние держит сам input, подсветку выбранного рисует CSS компонента
 * (:has(input:checked)), контроллеру остаётся только скрывать карточки.
 *
 * Каждая карточка несёт наборы токенов (data-level, data-purpose, data-audience,
 * data-language). Внутри одной категории фильтр работает как ИЛИ, между
 * категориями — как И: «новички» + «женские» означает женскую группу,
 * открытую новичку.
 *
 * Токенов в data-level может быть несколько: группа «любой уровень» подходит и
 * под «новичков», и под «продвинутых», и должна оставаться видимой в обоих
 * случаях — иначе фильтр по уровню прячет ровно те чаты, куда новичка и правда
 * зовут.
 */
export default class CommunityFilterController extends Controller {
  static targets = ['chip', 'card', 'group', 'count', 'empty', 'reset']
  // Шаблон счётчика «показано N из M» приходит переведённым из ui_translations
  // (data-*-shown-template-value): порядок слов в языках разный, и собирать
  // фразу из числа и хвоста в JS значило бы зафиксировать английский порядок.
  static values = { shownTemplate: String, total: Number }

  connect() {
    this.apply()
  }

  toggle() {
    this.apply()
  }

  reset() {
    this.chipTargets.forEach((chip) => {
      chip.checked = false
    })

    this.apply()
  }

  apply() {
    const active = new Map()

    this.chipTargets.forEach((chip) => {
      if (!chip.checked) return

      const { filterKind: kind, filterValue: value } = chip.dataset
      if (!active.has(kind)) active.set(kind, [])
      active.get(kind).push(value)
    })

    let visible = 0

    this.cardTargets.forEach((card) => {
      const matches = [...active].every(([kind, values]) => {
        const tokens = this.tokens(card, kind)
        return values.some((value) => tokens.includes(value))
      })

      card.hidden = !matches
      if (matches) visible += 1
    })

    // Заголовок клуба без единой видимой карточки — визуальный мусор.
    this.groupTargets.forEach((group) => {
      group.hidden = !this.cardTargets.some(
        (card) => group.contains(card) && !card.hidden
      )
    })

    if (this.hasCountTarget)
      this.countTarget.textContent = this.shownText(visible)
    if (this.hasEmptyTarget) this.emptyTarget.hidden = visible > 0
    // Сброс показываем только когда есть что сбрасывать — как в фильтрах каталогов.
    if (this.hasResetTarget) this.resetTarget.hidden = active.size === 0
  }

  tokens(card, kind) {
    return (card.dataset[kind] || '').split(' ').filter(Boolean)
  }

  shownText(visible) {
    return this.shownTemplateValue
      .replaceAll('%{count}', visible)
      .replaceAll('%{total}', this.totalValue)
  }
}
